import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@components/ui/Button';
import { useKvkkOnay, useSignOut } from './hooks/useAuth';

/**
 * KVKK Onay Modal — blocking overlay.
 *
 * AppLayout `me.kvkkAcikRiza === false` ise render eder; dashboard'a erişimi
 * checkbox onayı gelene kadar engeller. Dismiss yok — kullanıcının iki
 * seçeneği var:
 *   1. Onaylayıp devam et (POST /auth/kvkk-onay)
 *   2. Çıkış yap
 *
 * Yeni signup'lar zaten `kvkkAcikRiza: true` ile sync edildiği için bu
 * modal sadece migration'dan gelen veya manuel kvkkAcikRiza=false set
 * edilmiş tenant'larda görünür. Üretimde nadir, ama Faz B / mevzuat
 * güncellemesi senaryosunda kritik.
 */
export function KvkkOnayModal() {
  const [accepted, setAccepted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const onay = useKvkkOnay();
  const signOut = useSignOut();

  const handleSubmit = async () => {
    setError(null);
    try {
      await onay.mutateAsync();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Onay başarısız');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="w-full max-w-lg rounded-lg bg-background p-6 shadow-xl">
        <h2 className="text-xl font-semibold tracking-tight">
          KVKK Aydınlatma Metni Onayı Gerekli
        </h2>
        <p className="mt-3 text-sm text-muted-foreground">
          Hesabınızda KVKK açık rıza kaydı bulunmuyor. Sistemi kullanmaya devam
          edebilmek için Aydınlatma Metni'ni okuyup onaylamanız gerekir.
        </p>

        <div className="mt-5 rounded-md border border-primary/30 bg-primary/5 p-4 text-sm">
          📄{' '}
          <Link
            to="/kvkk"
            target="_blank"
            rel="noreferrer"
            className="font-medium text-primary hover:underline"
          >
            KVKK Aydınlatma Metni'ni yeni sekmede aç
          </Link>
        </div>

        <label className="mt-5 inline-flex w-fit items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            className="mt-1 h-4 w-4 rounded border-input text-primary focus:ring-ring"
            checked={accepted}
            onChange={(e) => setAccepted(e.target.checked)}
          />
          <span className="text-sm">
            KVKK Aydınlatma Metni'ni okudum, kişisel verilerimin belirtilen
            amaçlarla işlenmesine açık rıza veriyorum.
          </span>
        </label>

        {error && (
          <p className="mt-3 text-xs text-danger">{error}</p>
        )}

        <div className="mt-6 flex gap-3">
          <Button
            type="button"
            className="flex-1"
            disabled={!accepted || onay.isPending}
            onClick={handleSubmit}
          >
            {onay.isPending ? 'Kaydediliyor...' : 'Onaylıyorum'}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => signOut.mutate()}
            disabled={signOut.isPending}
          >
            Çıkış
          </Button>
        </div>
      </div>
    </div>
  );
}
